import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { GigsService } from 'app/features/user/gigs/services/gigs.service';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { DropdownModule } from 'primeng/dropdown';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  map,
  catchError,
  takeUntil,
} from 'rxjs/operators';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [CommonModule, AutoCompleteModule, FormsModule, DropdownModule, ReactiveFormsModule],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
})
export class SearchComponent implements OnInit, OnDestroy {
  value: string = '';
  items: string[] = [];
  selectedType: string = 'services';
  types = ['services', 'projects', 'service provider'];

  @Input() placeholderType: boolean = false;

  private searchSubject = new Subject<{ query: string; type: string }>();
  private destroy$ = new Subject<void>();

  constructor(
    private filter_service: GigsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(400),
        distinctUntilChanged((prev, curr) => prev.query === curr.query && prev.type === curr.type),
        switchMap(({ query, type }) => {
          if (!query || type === 'service provider') {
            return of([]);
          }

          if (type === 'services') {
            return this.filter_service.serviceSearch('?search=' + query).pipe(
              map((res: any) =>
                (res?.data || [])
                  .filter(
                    (item: any) =>
                      item.title?.toLowerCase().includes(query) ||
                      item.id?.toString().toLowerCase().includes(query)
                  )
                  .map((item: any) => item.title)
                  .slice(0, 6)
              ),
              catchError(() => of([]))
            );
          }

          if (type === 'projects') {
            return this.filter_service.projectSearch('?search=' + query).pipe(
              map((res: any) =>
                (res?.data || [])
                  .filter(
                    (item: any) =>
                      item.title?.toLowerCase().includes(query) ||
                      item.id?.toString().toLowerCase().includes(query)
                  )
                  .map((item: any) => item.title)
                  .slice(0, 6)
              ),
              catchError(() => of([]))
            );
          }

          return of([]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((results: string[]) => {
        this.items = results;
      });
  }

  clearInput() {
    this.value = '';
    this.items = [];
  }

  goSearchFilter() {
    const search = this.value || '';
    let targetUrl = '';

    if (this.selectedType === 'services') {
      targetUrl = `/client/${this.selectedType}`;
    } else if (this.selectedType === 'projects') {
      targetUrl = `/freelancer/${this.selectedType}`;
    } else {
      targetUrl = `/freelancers-list`;
    }

    if (this.router.url === targetUrl) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([targetUrl], {
          state: { search },
        });
      });
    } else {
      this.router.navigate([targetUrl], {
        state: { search },
      });
    }
  }

  search(event: AutoCompleteCompleteEvent) {
    const query = event.query?.trim().toLowerCase() || '';

    if (!query) {
      this.items = [];
      return;
    }

    this.searchSubject.next({
      query,
      type: this.selectedType,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
